// File: /src/pages/ResetPassword.js

import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Eye, EyeOff } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import apiClient from '../services/apiClient';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import '../styles/Login.css';
import '../styles/ResetPassword.css';

// Password validation: min 8 chars with uppercase, lowercase, number, and any special character
// Accepts ANY non-alphanumeric character as special (more flexible and user-friendly)
const PASSWORD_REGEX =
	/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{8,}$/;

const ResetPassword = () => {
	const navigate = useNavigate();
	const { isDarkMode } = useTheme();
	const [searchParams] = useSearchParams();
	const token = useMemo(
		() => searchParams.get('token') || '',
		[searchParams],
	);

	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!token) {
			toast.error('Token de redefinição ausente ou inválido.');
			return;
		}

		if (!password || !confirmPassword) {
			toast.error('Preencha a nova senha e a confirmação.');
			return;
		}

		if (password !== confirmPassword) {
			toast.error('As senhas não coincidem.');
			return;
		}

		if (!PASSWORD_REGEX.test(password)) {
			toast.error(
				'A senha deve ter no mínimo 8 caracteres, incluindo: 1 letra maiúscula, 1 minúscula, 1 número e 1 caractere especial.',
			);
			return;
		}

		try {
			setSubmitting(true);
			await apiClient.post(API_ENDPOINTS.AUTH_RESET_PASSWORD, {
				token,
				password,
			});
			toast.success(
				'Senha redefinida com sucesso! Faça login com a nova credencial.',
			);
			navigate('/login', { replace: true });
		} catch (error) {
			const message =
				error.response?.data?.error ||
				'Não foi possível redefinir a senha. Solicite um novo link.';
			toast.error(message);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className={`auth-page ${isDarkMode ? 'dark-mode' : ''}`}>
			<div className="auth-logo">
				<img src="/locatime1.png" alt="LocaTime" loading="lazy" />
			</div>
			<div className="login-container reset-password-container">
				<h2>Redefinir Senha</h2>
				{!token && (
					<p className="reset-password-warning">
						O link de redefinição parece inválido ou expirou. Solicite um
						novo e-mail na página de login.
					</p>
				)}

				<form onSubmit={handleSubmit}>
					<div className="password-input-wrapper">
						<input
							type={showPassword ? 'text' : 'password'}
							name="password"
							placeholder="Nova senha"
							value={password}
							onChange={(event) => setPassword(event.target.value)}
							disabled={submitting}
							required
						/>
						<span
							className="password-toggle-icon"
							onClick={() => setShowPassword(!showPassword)}
						>
							{showPassword ? (
								<EyeOff size={20} />
							) : (
								<Eye size={20} />
							)}
						</span>
					</div>
					<div className="password-input-wrapper">
						<input
							type={showConfirmPassword ? 'text' : 'password'}
							name="confirmPassword"
							placeholder="Confirmar nova senha"
							value={confirmPassword}
							onChange={(event) => setConfirmPassword(event.target.value)}
							disabled={submitting}
							required
						/>
						<span
							className="password-toggle-icon"
							onClick={() => setShowConfirmPassword(!showConfirmPassword)}
						>
							{showConfirmPassword ? (
								<EyeOff size={20} />
							) : (
								<Eye size={20} />
							)}
						</span>
					</div>
					<button type="submit" disabled={submitting || !token}>
						{submitting ? 'Redefinindo...' : 'Atualizar senha'}
					</button>
				</form>

				<p className="reset-password-footer">
					Lembrou sua senha? <Link to="/login">Voltar para o login</Link>
				</p>
			</div>
			<div className="auth-powered-by">
				<span>Powered by:</span>
				<a
					href="https://www.deeplynx.com.br/pt/"
					target="_blank"
					rel="noopener noreferrer"
					className="auth-powered-by__link"
				>
					<img
						src="/deeplynx.png"
						alt="DeepLynx"
						className="auth-powered-by__logo"
						loading="lazy"
					/>
				</a>
			</div>
		</div>
	);
};

export default ResetPassword;
